import { useCallback, useContext, useEffect, useState } from 'react';
import { ModalContext, BackdropLoadingContext } from './modalContext';
import { RMModalProps } from './RMModal';
import { ModalType } from './constants';

let activeModalId: string | undefined;
let isActiveModalVisible = false;

export const isModalOpen = (modalId: string) =>
  isActiveModalVisible && activeModalId === modalId;

export const useModalHandlers = () => {
  const modalContext = useContext(ModalContext);

  const hideModal = useCallback(() => {
    isActiveModalVisible = false;
    modalContext.setModalDetails((prevState) => ({
      ...prevState,
      showModal: false,
    }));
  }, [modalContext]);

  const showModal = useCallback(
    (props: Omit<RMModalProps, 'showModal' | 'setShowModal'>) => {
      activeModalId = props.id;
      isActiveModalVisible = true;
      modalContext.setModalDetails({
        type: ModalType.Alert,
        showModal: true,
        setShowModal: hideModal,
        ...props,
      });
    },
    [modalContext, hideModal],
  );

  const showAwaitableModal = useCallback(
    (
      props: Omit<RMModalProps, 'showModal' | 'setShowModal'> & {
        resolveModalOnHide?: boolean;
      },
    ) => {
      const { onOk, onHide, resolveModalOnHide = false, ...rest } = props;
      return new Promise<void>((resolve) => {
        activeModalId = rest.id;
        isActiveModalVisible = true;
        modalContext.setModalDetails({
          type: ModalType.Alert,
          showModal: true,
          setShowModal: () => {
            hideModal();
            resolve();
          },
          onOk: () => {
            if (onOk) {
              onOk();
            }
            resolve();
          },
          onHide: () => {
            hideModal();
            if (onHide) {
              onHide();
            }
            if (resolveModalOnHide) {
              resolve();
            }
          },
          ...rest,
        });
      });
    },
    [modalContext, hideModal],
  );

  const showLoadingModal = useCallback(
    (text: string) => {
      activeModalId = undefined;
      isActiveModalVisible = true;
      modalContext.setModalDetails({
        type: ModalType.Loading,
        showModal: true,
        text,
      });
    },
    [modalContext],
  );
  return { showModal, showAwaitableModal, hideModal, showLoadingModal };
};

export const useBackdropLoading = () => {
  const { showBackdropLoading, showBackdropDescription } =
    useContext(BackdropLoadingContext);
  const [isBackdropOpen, setOpen] = useState(false);

  useEffect(() => {
    showBackdropLoading(isBackdropOpen);
  }, [isBackdropOpen, showBackdropLoading]);

  const showBackdrop = useCallback(
    (desc: string) => {
      showBackdropDescription(desc);
      showBackdropLoading(true);
      setOpen(true);
    },
    [showBackdropDescription, showBackdropLoading],
  );

  const hideBackdrop = useCallback(() => {
    showBackdropLoading(false);
    setOpen(false);
    showBackdropDescription('Loading...');
  }, [showBackdropDescription, showBackdropLoading]);

  return { showBackdrop, hideBackdrop, isBackdropOpen };
};
